import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { TotemState, useGetTotemsFilterQuery } from 'data/__generated__';
import { TotemSelect } from 'components/TotemSelect';

type TotemSelectValue = {
    _id: string;
    name: string;
    fullname: string;
};

export function TotemOrganizationSelect({
    value,
    onChange,
    excludedTotemIds = [],
}: {
    value: string | null;
    onChange: (totem: TotemSelectValue | null) => void;
    excludedTotemIds?: string[];
}) {
    const [searchTerm, setSearchTerm] = useState(value || '');

    const { data: totemsData, refetch } = useGetTotemsFilterQuery({
        variables: {
            filters: {
                searchValue: searchTerm || undefined,
                state: [TotemState.Rollout],
            },
        },
        fetchPolicy: 'cache-and-network',
    });

    const totems = useMemo(() => totemsData?.totemsFilter.totems ?? [], [totemsData]);

    // The parent passes a fresh `excludedTotemIds` array on every render, so we keep the
    // latest value in a ref and read it inside `fetchOptions`. This keeps `fetchOptions`
    // referentially stable (dep: only the stable Apollo `refetch`); otherwise TotemSelect's
    // debounced search effect would restart every render and cancel its timer before it fires.
    const excludedTotemIdsRef = useRef(excludedTotemIds);
    excludedTotemIdsRef.current = excludedTotemIds;

    const options = useMemo(
        () =>
            totems
                .filter((totem) => !excludedTotemIds.includes(totem._id))
                .map((totem) => ({
                    label: totem.fullname,
                    value: totem._id,
                })),
        [totems, excludedTotemIds],
    );

    useEffect(() => {
        if (value && !options.some((option) => option.value === value)) {
            setSearchTerm(value);
        }
    }, [value, options]);

    const handleChange = (selectedId: string | null) => {
        if (!selectedId) {
            onChange(null);
            return;
        }
        const totem = totems.find((t) => t._id === selectedId);
        if (!totem) return;
        onChange({ _id: totem._id, name: totem.name ?? '', fullname: totem.fullname });
    };

    // Server-side search: fetch the totems matching the typed query and return them so
    // TotemSelect can display them (in async mode it renders the fetched results, not the
    // `options` prop). Returning [] here was the bug — typing emptied the list.
    const fetchOptions = useCallback(
        async (inputValue: string): Promise<{ label: string; value: string }[]> => {
            const result = await refetch({
                filters: {
                    searchValue: inputValue || undefined,
                    state: [TotemState.Rollout],
                },
            });
            return (result.data?.totemsFilter.totems ?? [])
                .filter((totem) => !excludedTotemIdsRef.current.includes(totem._id))
                .map((totem) => ({
                    label: totem.fullname,
                    value: totem._id,
                }));
        },
        [refetch],
    );

    return (
        <div className="w-full">
            <TotemSelect<string>
                placeholder="Rechercher un totem..."
                value={value}
                options={options}
                onChange={handleChange}
                fetchOptions={fetchOptions}
                defaultOptions={options}
            />
        </div>
    );
}
